import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  Chip,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { apiConfig } from "../../../../utils/apiConfig";

// --- Constants and Utilities ---
const INCOME_COLORS = {
  "Social Security": "#00bcd4",
  Pension: "#2196f3",
  "Retirement Account Distributions": "#3f51b5",
  "W-2 Wage Income": "#673ab7",
  "1099 Income": "#9c27b0",
  Dividends: "#e91e63",
  "Long Term Capital Gain": "#f44336",
  "Short Term Capital Gain": "#ff5722",
  "Royalty Income": "#ff9800",
  "Interest Income": "#ffc107",
  Inheritance: "#ffeb3b",
  "Rental Income": "#cddc39",
  "Rental income (Schedule E)": "#cddc39",
  "Business Income (Schedule C)": "#8bc34a",
  "Gig Economy and Platform Income (1099-K)": "#9e9e9e",
  Other: "#8bc34a",
  Unknown: "#9e9e9e",
};

// Strict year filtering: include if (tax_year == year) OR (start_date <= Dec 31 of year AND end_date >= Jan 1 of year)
const filterRecordsForYear = (records, year) => {
  const jan1 = new Date(`${year}-01-01`);
  const dec31 = new Date(`${year}-12-31`);
  return records.filter((entry) => {
    if (entry.tax_year && parseInt(entry.tax_year) === year) return true;
    const start = entry.start_date ? new Date(entry.start_date) : null;
    const end = entry.end_date ? new Date(entry.end_date) : null;
    if (start && end) {
      return start <= dec31 && end >= jan1;
    }
    return false;
  });
};

const normalizeIncomeTypeName = (fullName) => {
  if (!fullName) return "Unknown";
  const nameMap = {
    "Social Security Benefits (Taxable Portion)": "Social Security",
    "Public Pension Income (Government or Military Pension)": "Pension",
    "Private Pension Income": "Pension",
    "Retirement Account Distributions (1099-R)":
      "Retirement Account Distributions",
    "W-2 Wage Income": "W-2 Wage Income",
    "1099 Income": "1099 Income",
    "Dividends (Qualified and Ordinary)": "Dividends",
    "Capital Gains": "Long Term Capital Gain",
    "Interest Income": "Interest Income",
    "Rental Income": "Rental Income",
    "Rental income (Schedule E)": "Rental income (Schedule E)",
    "Business Income (Schedule C)": "Business Income (Schedule C)",
    "Gig Economy and Platform Income (1099-K)":
      "Gig Economy and Platform Income (1099-K)",
    "Other Income": "Other",
  };
  return nameMap[fullName] || fullName;
};
const formatCurrency = (v) =>
  `$${parseFloat(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const getAvailableYearsStrict = (records) => {
  const years = new Set();
  records.forEach((entry) => {
    if (entry.tax_year) {
      years.add(parseInt(entry.tax_year));
    } else if (entry.start_date && entry.end_date) {
      const startYear = new Date(entry.start_date).getFullYear();
      const endYear = new Date(entry.end_date).getFullYear();
      for (let y = startYear; y <= endYear; y++) {
        years.add(y);
      }
    }
  });
  return Array.from(years)
    .filter(Boolean)
    .sort((a, b) => a - b);
};
const groupByMember = (records, membersMap) => {
  const grouped = {};
  records.forEach((entry) => {
    const key = entry.user_id ? entry.user_id : "Household";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(entry);
  });
  return grouped;
};

// --- Toast Notification ---
const Toast = ({ message, type, onClose }) => (
  <Box
    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg dark:shadow-none z-50 ${type === "error" ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"}`}
    sx={{ display: "flex", alignItems: "center" }}
  >
    {type === "error" ? (
      <ExclamationCircleIcon className="w-5 h-5 mr-2" />
    ) : (
      <CheckCircleIcon className="w-5 h-5 mr-2" />
    )}
    <span>{message}</span>
    <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
      <XCircleIcon className="w-5 h-5" />
    </IconButton>
  </Box>
);

// --- Year Toggle ---
const YearToggle = ({ years, selectedYear, onYearChange }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="body2" color="text.secondary">
      Tax Year:
    </Typography>
    {years.map((year) => (
      <Button
        key={year}
        variant={selectedYear === year ? "contained" : "outlined"}
        size="small"
        onClick={() => onYearChange(year)}
      >
        {year}
      </Button>
    ))}
  </Stack>
);

// --- Donut Chart ---
const DonutChart = ({ data, total, onSegmentClick }) => (
  <ResponsiveContainer width={220} height={220}>
    <PieChart>
      <Pie
        data={data}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="50%"
        innerRadius={60}
        outerRadius={90}
        onClick={(_, idx) => onSegmentClick && onSegmentClick(data[idx])}
      >
        {data.map((entry, idx) => (
          <Cell key={`cell-${idx}`} fill={entry.color} />
        ))}
      </Pie>
      <RechartsTooltip formatter={(v, n) => [formatCurrency(v), n]} />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="18"
        fontWeight="bold"
      >
        {formatCurrency(total)}
      </text>
    </PieChart>
  </ResponsiveContainer>
);

// --- Bar Chart ---
const IncomeBarChart = ({ incomeData, availableYears }) => {
  // Get all possible income types from the data
  const allIncomeTypes = Array.from(
    new Set(
      incomeData.map((income) =>
        normalizeIncomeTypeName(income.income_type_name),
      ),
    ),
  );
  // For each year in availableYears, aggregate income data using strict filter
  const processedData = availableYears.map((year) => {
    const yearRecords = filterRecordsForYear(incomeData, year);
    const yearData = { year };
    allIncomeTypes.forEach((type) => {
      yearData[type] = yearRecords
        .filter(
          (income) => normalizeIncomeTypeName(income.income_type_name) === type,
        )
        .reduce(
          (sum, income) =>
            sum + parseFloat(income.gross_amount || income.amount || 0),
          0,
        );
    });
    return yearData;
  });
  if (
    allIncomeTypes.length === 0 ||
    processedData.every((yearData) => Object.keys(yearData).length <= 1)
  ) {
    return (
      <Box className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow mb-6">
        <Typography>
          No income data available for the selected year(s).
        </Typography>
      </Box>
    );
  }
  return (
    <Box className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow mb-6">
      <Typography variant="h6" mb={2}>
        Income Distribution Over Time
      </Typography>
      <Box height={400}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={processedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              label={{
                value: "Annual Amount ($)",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <RechartsTooltip
              formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
              labelFormatter={(label) => `Year: ${label}`}
            />
            <Legend />
            {allIncomeTypes.map((type) => (
              <Bar
                key={type}
                dataKey={type}
                name={type}
                stackId="income"
                fill={INCOME_COLORS[type] || "#8bc34a"}
                opacity={1}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

// --- Sidebar Drawer ---
const IncomeDrawer = ({ open, onClose, member, incomes, onEdit, onDelete }) => {
  const [editingIncome, setEditingIncome] = useState(null);

  const handleEdit = (income) => {
    setEditingIncome(income);
  };

  const handleSave = () => {
    // TODO: Implement save logic
    setEditingIncome(null);
  };

  const handleCancel = () => {
    setEditingIncome(null);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 500 }, p: 3 } }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {member?.name || "Income Details"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <XCircleIcon className="w-5 h-5" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                  Income Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                  Frequency
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {incomes.map((rec, idx) => (
                <tr
                  key={rec.client_income_id || rec.income_id || idx}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="px-4 py-2 whitespace-nowrap">
                    {editingIncome?.client_income_id ===
                    rec.client_income_id ? (
                      <select
                        className="w-full p-1 border rounded"
                        value={editingIncome.income_type_name}
                        onChange={(e) =>
                          setEditingIncome({
                            ...editingIncome,
                            income_type_name: e.target.value,
                          })
                        }
                      >
                        {Object.keys(INCOME_COLORS).map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Chip
                        label={
                          normalizeIncomeTypeName(rec.income_type_name) ||
                          "Unknown"
                        }
                        size="small"
                        sx={{
                          bgcolor: `${INCOME_COLORS[normalizeIncomeTypeName(rec.income_type_name)] || INCOME_COLORS.Other}15`,
                          color:
                            INCOME_COLORS[
                              normalizeIncomeTypeName(rec.income_type_name)
                            ] || INCOME_COLORS.Other,
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {editingIncome?.client_income_id ===
                    rec.client_income_id ? (
                      <input
                        type="number"
                        className="w-full p-1 border rounded"
                        value={
                          editingIncome.gross_amount ||
                          editingIncome.amount ||
                          0
                        }
                        onChange={(e) =>
                          setEditingIncome({
                            ...editingIncome,
                            gross_amount: e.target.value,
                          })
                        }
                      />
                    ) : (
                      formatCurrency(rec.gross_amount || rec.amount || 0)
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {editingIncome?.client_income_id ===
                    rec.client_income_id ? (
                      <select
                        className="w-full p-1 border rounded"
                        value={
                          editingIncome.frequency_name ||
                          editingIncome.frequency_id ||
                          ""
                        }
                        onChange={(e) =>
                          setEditingIncome({
                            ...editingIncome,
                            frequency_name: e.target.value,
                          })
                        }
                      >
                        <option value="Annual">Annual</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Bi-Weekly">Bi-Weekly</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Daily">Daily</option>
                      </select>
                    ) : (
                      rec.frequency_name || rec.frequency_id || "N/A"
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {editingIncome?.client_income_id ===
                    rec.client_income_id ? (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={handleSave}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleEdit(rec)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => onDelete && onDelete(rec)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              // TODO: Implement add new income logic
            }}
          >
            Add New Income
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

// --- Main Component ---
const IncomeBenefits = () => {
  const [householdId, setHouseholdId] = useState(null);
  const [incomeData, setIncomeData] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMember, setDrawerMember] = useState(null);
  const [drawerIncomes, setDrawerIncomes] = useState([]);

  // Fetch householdId from URL if using react-router (adjust as needed)
  useEffect(() => {
    const url = window.location.pathname;
    const match = url.match(/household-profile\/(\d+)/);
    if (match) setHouseholdId(match[1]);
  }, []);

  // Compute available years: only years with at least one record passing the strict filter
  const computeAvailableYearsWithData = (records) => {
    // Get all possible years from the data
    const possibleYears = new Set();
    records.forEach((entry) => {
      if (entry.tax_year) {
        possibleYears.add(parseInt(entry.tax_year));
      } else if (entry.start_date && entry.end_date) {
        const startYear = new Date(entry.start_date).getFullYear();
        const endYear = new Date(entry.end_date).getFullYear();
        for (let y = startYear; y <= endYear; y++) {
          possibleYears.add(y);
        }
      }
    });
    // Only include years with at least one record passing the strict filter
    return Array.from(possibleYears)
      .filter((year) => filterRecordsForYear(records, year).length > 0)
      .sort((a, b) => a - b);
  };

  // Fetch all years income data and members
  useEffect(() => {
    if (!householdId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      apiConfig.api.get(
        `/advisor-portal/income-benefits/all-with-history/${householdId}`,
      ),
      apiConfig.api.get(
        `/advisor-portal/client-database/household-profile/${householdId}`,
      ),
    ])
      .then(([incomeRes, membersRes]) => {
        // Use only the history array to avoid double-counting
        const allRecords = incomeRes.data.history || [];
        setIncomeData(allRecords);
        setMembers(membersRes.data.members || []);
        // Only include years that actually have data (after strict filtering)
        const years = computeAvailableYearsWithData(allRecords);
        setAvailableYears(years);
        if (years.length > 0) setSelectedYear(years[years.length - 1]);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to fetch income data");
        setLoading(false);
      });
  }, [householdId]);

  // Deduplicate records by unique key
  const dedupeRecords = (records) => {
    const seen = new Set();
    return records.filter((rec) => {
      const key = rec.client_income_id || rec.income_id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Replace recordsForYear logic
  const recordsForYear = useMemo(
    () =>
      selectedYear
        ? dedupeRecords(filterRecordsForYear(incomeData, selectedYear))
        : [],
    [incomeData, selectedYear],
  );

  // Group by member
  const membersMap = useMemo(
    () => Object.fromEntries(members.map((m) => [m.userId, m])),
    [members],
  );
  const grouped = useMemo(
    () => groupByMember(recordsForYear, membersMap),
    [recordsForYear, membersMap],
  );

  // Donut chart data for each member/household
  const donutDataByMember = useMemo(() => {
    const result = {};
    Object.entries(grouped).forEach(([key, records]) => {
      const typeGroups = records.reduce((acc, income) => {
        const typeName = normalizeIncomeTypeName(income.income_type_name);
        acc[typeName] =
          (acc[typeName] || 0) +
          parseFloat(income.gross_amount || income.amount || 0);
        return acc;
      }, {});
      result[key] = Object.entries(typeGroups).map(([name, value]) => ({
        name,
        value,
        color: INCOME_COLORS[name] || INCOME_COLORS.Other,
      }));
    });
    return result;
  }, [grouped]);

  // Total for each member/household
  const totalByMember = useMemo(() => {
    const result = {};
    Object.entries(grouped).forEach(([key, records]) => {
      result[key] = records.reduce(
        (sum, income) =>
          sum + parseFloat(income.gross_amount || income.amount || 0),
        0,
      );
    });
    return result;
  }, [grouped]);

  // Sidebar popout logic
  const handleRowClick = useCallback(
    (memberKey) => {
      setDrawerMember(
        memberKey === "Household"
          ? { name: "Household (Joint)" }
          : membersMap[memberKey],
      );
      setDrawerIncomes(grouped[memberKey] || []);
      setDrawerOpen(true);
    },
    [grouped, membersMap],
  );

  if (loading)
    return (
      <Box className="p-8 text-center">
        <CircularProgress />
      </Box>
    );
  if (error) return <Box className="p-8 text-center text-red-500">{error}</Box>;

  return (
    <Box className="space-y-6">
      <Typography variant="h5" mb={4}>
        Income & Benefits
      </Typography>
      <IncomeBarChart incomeData={incomeData} availableYears={availableYears} />
      <Box className="flex items-center justify-between mb-4">
        <YearToggle
          years={availableYears}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlusIcon className="w-5 h-5" />}
          onClick={() => {
            setDrawerMember({ name: "New Income" });
            setDrawerIncomes([]);
            setDrawerOpen(true);
          }}
        >
          Add Income
        </Button>
      </Box>
      {recordsForYear.length === 0 ? (
        <Box className="p-8 text-center text-gray-500 dark:text-gray-500">
          No income data found for {selectedYear}.
        </Box>
      ) : (
        <Box className="bg-surface-default dark:bg-surface-default shadow rounded-lg p-6">
          <Stack direction={{ xs: "column", md: "row" }} spacing={4} mb={4}>
            {Object.entries(grouped).map(([key, records]) => (
              <Box
                key={key}
                sx={{ minWidth: 220, flex: 1, cursor: "pointer" }}
                onClick={() => handleRowClick(key)}
              >
                <Typography
                  variant="subtitle1"
                  mb={1}
                  fontWeight={600}
                  color="text.primary"
                >
                  {key === "Household"
                    ? "Household (Joint)"
                    : `${membersMap[key]?.firstName || ""} ${membersMap[key]?.lastName || ""}`}
                </Typography>
                <DonutChart
                  data={donutDataByMember[key]}
                  total={totalByMember[key]}
                />
              </Box>
            ))}
          </Stack>
          <Box>
            {Object.entries(grouped).map(([key, records]) => {
              const member = key === "Household" ? null : membersMap[key];
              return (
                <Box key={key} className="mb-6">
                  <Box className="flex justify-between items-center mb-2">
                    <Typography variant="h6" color="text.primary">
                      {member
                        ? `${member.firstName} ${member.lastName}`
                        : "Household (Joint)"}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<PlusIcon className="w-4 h-4" />}
                      onClick={() => {
                        setDrawerMember(
                          member || { name: "Household (Joint)" },
                        );
                        setDrawerIncomes([]);
                        setDrawerOpen(true);
                      }}
                    >
                      Add Income
                    </Button>
                  </Box>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                          Income Type
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                          Amount
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                          Frequency
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((rec, idx) => (
                        <tr
                          key={rec.client_income_id || rec.income_id || idx}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                          onClick={() => {
                            setDrawerMember(
                              member || { name: "Household (Joint)" },
                            );
                            setDrawerIncomes(records);
                            setDrawerOpen(true);
                          }}
                        >
                          <td className="px-4 py-2 whitespace-nowrap">
                            <Chip
                              label={
                                normalizeIncomeTypeName(rec.income_type_name) ||
                                "Unknown"
                              }
                              size="small"
                              sx={{
                                bgcolor: `${INCOME_COLORS[normalizeIncomeTypeName(rec.income_type_name)] || INCOME_COLORS.Other}15`,
                                color:
                                  INCOME_COLORS[
                                    normalizeIncomeTypeName(
                                      rec.income_type_name,
                                    )
                                  ] || INCOME_COLORS.Other,
                                fontWeight: 500,
                              }}
                            />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {formatCurrency(
                              rec.gross_amount || rec.amount || 0,
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {rec.frequency_name || rec.frequency_id || "N/A"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <IconButton size="small" color="primary">
                              <PencilIcon className="w-4 h-4" />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <IncomeDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        member={drawerMember}
        incomes={drawerIncomes}
        onEdit={(income) => {
          // TODO: Implement edit logic
          setToast({ message: "Income updated successfully", type: "success" });
        }}
        onDelete={(income) => {
          // TODO: Implement delete logic
          setToast({ message: "Income deleted successfully", type: "success" });
        }}
      />
    </Box>
  );
};

export default IncomeBenefits;
